// French-localised formatters used across HejPoland.
// Date format target: "1 juin 2026" (no leading zero, lowercase month).
// Number format target: "15 900,00 zł" (NBSP thousands, comma decimals).

const FR_MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

/**
 * Format an ISO-ish date string ("YYYY-MM-DD" or full ISO) to "1 juin 2026".
 * Returns "—" for empty/invalid input.
 */
export function formatDateFr(value) {
  if (!value) return "—";
  // Accept "YYYY-MM-DD" plain to avoid TZ shift surprises.
  const m = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let day, monthIdx, year;
  if (m) {
    year = Number(m[1]);
    monthIdx = Number(m[2]) - 1;
    day = Number(m[3]);
  } else {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    day = d.getDate();
    monthIdx = d.getMonth();
    year = d.getFullYear();
  }
  if (monthIdx < 0 || monthIdx > 11) return "—";
  return `${day} ${FR_MONTHS[monthIdx]} ${year}`;
}

/**
 * Format a number as "15 900,00 zł" (or any provided unit).
 * - Always uses NBSP (\u00A0) for thousands.
 * - Trims trailing zeros to keep integers clean ("100 zł" not "100,00 zł").
 * - Returns "—" when value is null/undefined/NaN.
 */
export function formatMoneyFr(value, unit = "zł", { decimals = "auto" } = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const isInt = Number.isInteger(n);
  const fractionDigits = decimals === "auto" ? (isInt ? 0 : 2) : decimals;
  // Use fr-FR locale then replace narrow NBSP/regular space variants with a consistent NBSP.
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n).replace(/\s/g, "\u00A0");
  return unit ? `${formatted}\u00A0${unit}` : formatted;
}

/**
 * Format a single indicator value: avg as the headline, min-max as fourchette.
 * Returns { primary, range } where primary is always defined ("—" if avg is null).
 */
export function formatIndicator(ind) {
  if (!ind) return { primary: "—", range: "" };
  const unit = ind.unit || "";
  const primary = formatMoneyFr(ind.avg, unit);
  const hasMin = ind.min !== null && ind.min !== undefined;
  const hasMax = ind.max !== null && ind.max !== undefined;
  if (!hasMin && !hasMax) return { primary, range: "" };
  const minStr = hasMin ? formatMoneyFr(ind.min, "") : "—";
  const maxStr = hasMax ? formatMoneyFr(ind.max, "") : "—";
  return { primary, range: `${minStr}\u00A0–\u00A0${maxStr}\u00A0${unit}`.trim() };
}
